/**
 * Utilidades Canónicas del Sistema de Impresión Bimodal Sanesca PRO (PLAN-030)
 * Proporciona helpers para portal DOM (#print-portal) y apertura de ventanas limpias aisladas.
 */

export interface CleanPrintWindowOptions {
  title: string;
  contentHtml: string;
  pageSize?: 'letter portrait' | 'letter landscape';
  margin?: string;
  extraStyles?: string;
}

/**
 * Obtiene el nodo DOM objetivo para React createPortal.
 * Busca preferentemente #print-portal (hermano de #root), con fallback a document.body.
 */
export const getPrintPortal = (): HTMLElement | null => {
  if (typeof document === 'undefined') return null;
  return document.getElementById('print-portal') || document.body;
};

/**
 * Abre una ventana emergente limpia e independiente (_blank) con documento HTML autónomo.
 * Inyecta Tailwind CSS, estilos de reseteo blanco, reglas @page y barra de acciones no-print.
 * Inmune a estilos oscuros de la SPA y eficaz ante bloqueos de diálogo nativo en tablets/móviles.
 */
export const openCleanPrintWindow = ({
  title,
  contentHtml,
  pageSize = 'letter portrait',
  margin = '8mm',
  extraStyles = ''
}: CleanPrintWindowOptions): boolean => {
  if (typeof window === 'undefined') return false;

  const printWindow = window.open('', '_blank', 'width=980,height=900,menubar=no,toolbar=no,location=no,status=no');
  if (!printWindow) {
    alert('Por favor permita las ventanas emergentes (pop-ups) en el navegador para abrir el documento de impresión limpio.');
    return false;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${title} • Sanesca Industrial PRO</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <style>
          @page {
            size: ${pageSize};
            margin: ${margin};
          }
          * {
            box-sizing: border-box;
          }
          body {
            background: #ffffff !important;
            color: #000000 !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            margin: 0;
            padding: 12px;
          }
          .avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          @media print {
            .no-print-action-bar {
              display: none !important;
            }
            body {
              padding: 0 !important;
            }
          }
          ${extraStyles}
        </style>
      </head>
      <body>
        <div class="no-print-action-bar" style="padding: 12px 16px; background: #0f172a; color: #ffffff; border-radius: 8px; margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
          <div style="font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 8px;">
            <span style="display: inline-block; width: 8px; height: 8px; background: #0284c7; border-radius: 50%;"></span>
            <span>${title}</span>
            <span style="font-size: 11px; opacity: 0.7; font-weight: normal;">(Formato Carta Letter)</span>
          </div>
          <div style="display: flex; gap: 8px;">
            <button onclick="window.print()" style="background: #0284c7; hover: background: #0369a1; color: #ffffff; border: none; padding: 6px 14px; border-radius: 6px; font-size: 12px; font-weight: bold; cursor: pointer; display: flex; align-items: center; gap: 4px;">
              🖨️ Mandar a Imprimir
            </button>
            <button onclick="window.close()" style="background: #334155; color: #e2e8f0; border: none; padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer;">
              ✕ Cerrar
            </button>
          </div>
        </div>

        <div style="width: 215.9mm; max-width: 100%; margin: 0 auto;">
          ${contentHtml}
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  return true;
};
